import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import InvoiceGenerator from './pages/InvoiceGenerator';
import AdminLogin from './pages/AdminLogin';
import { AdminLayout } from './components/AdminLayout';
import WhatsAppFloat from './components/WhatsAppFloat';
import { Toaster } from './components/ui/sonner';

// Lazy load admin pages
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminEnquiries = React.lazy(() => import('./pages/admin/AdminEnquiries'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminBlogs = React.lazy(() => import('./pages/admin/AdminBlogs'));
const AdminLeads = React.lazy(() => import('./pages/admin/AdminLeads'));
const CreateLead = React.lazy(() => import('./pages/admin/CreateLead'));
const BulkUploadLeads = React.lazy(() => import('./pages/admin/BulkUploadLeads'));
const LeadDetail = React.lazy(() => import('./pages/admin/LeadDetail'));
const AdminServices = React.lazy(() => import('./pages/admin/AdminServices'));
const AdminDocuments = React.lazy(() => import('./pages/admin/AdminDocuments'));
const AdminMobileApp = React.lazy(() => import('./pages/admin/AdminMobileApp'));
const MobileLayout = React.lazy(() => import('./pages/mobile/MobileLayout'));
const MobileLogin = React.lazy(() => import('./pages/mobile/MobileLogin'));
const MobileDashboard = React.lazy(() => import('./pages/mobile/MobileDashboard'));
const MobileLeads = React.lazy(() => import('./pages/mobile/MobileLeads'));
const MobileLeadDetail = React.lazy(() => import('./pages/mobile/MobileLeadDetail'));
const MobileFollowups = React.lazy(() => import('./pages/mobile/MobileFollowups'));
const MobileMore = React.lazy(() => import('./pages/mobile/MobileMore'));
const MobileDialer = React.lazy(() => import('./pages/mobile/MobileDialer'));
const ErpLogin = React.lazy(() => import('./pages/erp/ErpLogin'));
const ErpHeadAdmin = React.lazy(() => import('./pages/erp/ErpHeadAdmin'));
const ErpLayout = React.lazy(() => import('./pages/erp/ErpLayout'));
const ErpDashboard = React.lazy(() => import('./pages/erp/ErpDashboard'));
const ErpMasters = React.lazy(() => import('./pages/erp/ErpMasters'));
const ErpDocs = React.lazy(() => import('./pages/erp/ErpDocs'));
const ErpExpenses = React.lazy(() => import('./pages/erp/ErpExpenses'));
const ErpMoney = React.lazy(() => import('./pages/erp/ErpMoney'));
const ErpBanking = React.lazy(() => import('./pages/erp/ErpBanking'));
const ErpReports = React.lazy(() => import('./pages/erp/ErpReports'));
const ErpSettings = React.lazy(() => import('./pages/erp/ErpSettings'));
const ErpSubscription = React.lazy(() => import('./pages/erp/ErpSubscription'));
const ErpImportExport = React.lazy(() => import('./pages/erp/ErpImportExport'));
const ErpGst = React.lazy(() => import('./pages/erp/ErpGst'));
const ErpAccounting = React.lazy(() => import('./pages/erp/ErpAccounting'));
const ErpBulkBilling = React.lazy(() => import('./pages/erp/ErpBulkBilling'));
const ErpLandingPage = React.lazy(() => import('./pages/ErpLandingPage'));
const ErpPrint = React.lazy(() => import('./pages/erp/ErpPrint'));

const S = ({ children }) => (
  <React.Suspense fallback={<div className="min-h-screen bg-[#F1F4F9] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#0A2463] border-t-transparent rounded-full animate-spin" /></div>}>
    {children}
  </React.Suspense>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:blogId" element={<BlogDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/invoice-generator" element={<InvoiceGenerator />} />
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Utkarshudyam ERP */}
            <Route path="/erp/login" element={<S><ErpLogin /></S>} />
            <Route path="/utkarshudyam-erp" element={<S><ErpLandingPage /></S>} />
            <Route path="/erp/admin" element={<S><ErpHeadAdmin /></S>} />
            <Route path="/erp/print/:kind/:docId" element={<S><ErpPrint /></S>} />
            <Route path="/erp" element={<S><ErpLayout /></S>}>
              <Route index element={<S><ErpDashboard /></S>} />
              <Route path="customers" element={<S><ErpMasters kind="customers" /></S>} />
              <Route path="vendors" element={<S><ErpMasters kind="vendors" /></S>} />
              <Route path="items" element={<S><ErpMasters kind="items" /></S>} />
              <Route path="quotations" element={<S><ErpDocs kind="quotations" /></S>} />
              <Route path="invoices" element={<S><ErpDocs kind="invoices" /></S>} />
              <Route path="purchases" element={<S><ErpDocs kind="purchases" /></S>} />
              <Route path="expenses" element={<S><ErpExpenses /></S>} />
              <Route path="receipts" element={<S><ErpMoney kind="receipt" /></S>} />
              <Route path="payments" element={<S><ErpMoney kind="payment" /></S>} />
              <Route path="banking" element={<S><ErpBanking /></S>} />
              <Route path="reports" element={<S><ErpReports /></S>} />
              <Route path="settings" element={<S><ErpSettings /></S>} />
              <Route path="subscription" element={<S><ErpSubscription /></S>} />
              <Route path="importexport" element={<S><ErpImportExport /></S>} />
              <Route path="gst" element={<S><ErpGst /></S>} />
              <Route path="accounting" element={<S><ErpAccounting /></S>} />
              <Route path="bulk-billing" element={<S><ErpBulkBilling /></S>} />
            </Route>

            {/* Mobile CRM App (PWA) */}
            <Route path="/m/login" element={<S><MobileLogin /></S>} />
            <Route path="/m" element={<S><MobileLayout /></S>}>
              <Route index element={<S><MobileDashboard /></S>} />
              <Route path="dashboard" element={<S><MobileDashboard /></S>} />
              <Route path="leads" element={<S><MobileLeads /></S>} />
              <Route path="lead/:lead_number" element={<S><MobileLeadDetail /></S>} />
              <Route path="dialer" element={<S><MobileDialer /></S>} />
              <Route path="followups" element={<S><MobileFollowups /></S>} />
              <Route path="more" element={<S><MobileMore /></S>} />
            </Route>
            
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <AdminDashboard />
                </React.Suspense>
              } />
              <Route path="enquiries" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <AdminEnquiries />
                </React.Suspense>
              } />
              <Route path="users" element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AdminUsers />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="blogs" element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AdminBlogs />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="leads" element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AdminLeads />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="leads/create" element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CreateLead />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="leads/bulk-upload" element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <BulkUploadLeads />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="lead/:lead_number" element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <LeadDetail />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="services" element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AdminServices />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="documents" element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AdminDocuments />
                  </React.Suspense>
                </ProtectedRoute>
              } />
              <Route path="mobile-app" element={
                <React.Suspense fallback={<div>Loading...</div>}>
                  <AdminMobileApp />
                </React.Suspense>
              } />
            </Route>
          </Routes>
          <WhatsAppFloat />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;